// ════════════════════════════════════════════════════════════════════════════
// recruitmentShared.js — Single source of truth for recruiter-side API calls.
//
// Import in each screen:
//   import { RecruitmentAPI, STATUS_CFG, STAGES, normaliseApplicant } from './recruitmentShared';
//
// Every method here points at an endpoint that EXISTS on the server today
// (verified against /var/www/jobeng2024/jobeng-show-all-api.js). The
// previous version called many aspirational `/api/recruitment/*` routes that
// had no implementation; this version uses the canonical /api/data/* CRUD
// routes plus the few enriched JOIN endpoints we added (pipeline, applicant,
// timeline, notes, bulk-stage).
//
// All HTTP goes through the centralized apiFetch from utils/apiClient so we
// inherit its auth handling, base URL, and error normalisation — same
// behaviour the candidate-side screens use.
// ════════════════════════════════════════════════════════════════════════════

import AsyncStorage from '@react-native-async-storage/async-storage';
import { apiFetch } from '../../../utils/apiClient';

// ── Helpers ────────────────────────────────────────────────────────────────
const qs = (params = {}) =>
  Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');

const get  = (path)         => apiFetch(path);
const post = (path, body)   => apiFetch(path, { method: 'POST', body: JSON.stringify(body || {}) });
const put  = (path, body)   => apiFetch(path, { method: 'PUT',  body: JSON.stringify(body || {}) });
const del  = (path)         => apiFetch(path, { method: 'DELETE' });

// ── Recruitment API map ────────────────────────────────────────────────────
export const RecruitmentAPI = {
  // ── Pipeline (the headline call) ─────────────────────────────────────────
  // Returns applications JOINed with jobs + company_profiles + users +
  // interview counts. One round-trip → the recruiter sees everything they
  // need in their list view.
  getPipeline: (companyId, filters = {}) =>
    get(`/api/recruitment/pipeline?${qs({ company_id: companyId, ...filters })}`),

  getPipelineStats: (companyId) =>
    get(`/api/recruitment/pipeline/stats?company_id=${companyId}`),

  // ── Applicants ───────────────────────────────────────────────────────────
  // Legacy alias kept for screens that already call getApplicants. Same data
  // as getPipeline since "applicants" === applications scoped to the company.
  getApplicants: (params = {}) => {
    if (params.company_id) return get(`/api/recruitment/pipeline?${qs(params)}`);
    return get(`/api/data/applications?${qs(params)}`);
  },
  getApplicantsByJob: (jobId, params = {}) =>
    get(`/api/data/applications?${qs({ job_id: jobId, ...params })}`),

  // Single applicant — JOINed shape with inline interviews + offers
  getApplicantById: (id) => get(`/api/recruitment/applicant/${id}`),

  updateApplicant: (id, data) => put(`/api/data/applications/${id}`, data),
  deleteApplicant: (id)       => del(`/api/data/applications/${id}`),

  // ── Stage / workflow ─────────────────────────────────────────────────────
  // Tracked in application_timeline. Falls back to /api/data PUT if the
  // timeline endpoint isn't reachable.
  updateStage: (id, stage, opts = {}) =>
    post(`/api/recruitment/applicant/${id}/stage`, { stage, status: opts.status, note: opts.note })
      .catch(() => put(`/api/data/applications/${id}`, { stage, status: opts.status || stage })),

  advanceCandidate: (id, currentStage) => {
    const order = ['new', 'reviewed', 'shortlisted', 'interviewed', 'technical', 'offered', 'hired'];
    const idx = order.indexOf(currentStage);
    const next = idx >= 0 && idx < order.length - 1 ? order[idx + 1] : order[order.length - 1];
    return RecruitmentAPI.updateStage(id, next);
  },

  bulkUpdateStage: (ids, stage, status) =>
    post(`/api/recruitment/applicants/bulk-stage`, { applicant_ids: ids, stage, status }),

  bulkDelete: (ids) =>
    Promise.all(ids.map(id => del(`/api/data/applications/${id}`))),

  // ── Bookmark / flag / star — stored on the application row ──────────────
  toggleBookmark: async (id) => {
    const cur = await get(`/api/data/applications/${id}`);
    const row = Array.isArray(cur) ? cur[0] : (cur?.data?.[0] || cur?.data || cur);
    return put(`/api/data/applications/${id}`, { is_starred: !row?.is_starred });
  },
  toggleFlag: async (id) => {
    const cur = await get(`/api/data/applications/${id}`);
    const row = Array.isArray(cur) ? cur[0] : (cur?.data?.[0] || cur?.data || cur);
    return put(`/api/data/applications/${id}`, { is_flagged: !row?.is_flagged });
  },

  // ── Notes (threaded, multi-recruiter) ────────────────────────────────────
  getNotes: (id) => get(`/api/recruitment/applicant/${id}/notes`),
  addNote: async (id, text) => {
    const author_name = (await AsyncStorage.getItem('user_full_name')) || null;
    const author_id   = parseInt((await AsyncStorage.getItem('user_id')) || '0', 10) || null;
    return post(`/api/recruitment/applicant/${id}/notes`, { content: text, author_id, author_name });
  },

  // ── Rating ──────────────────────────────────────────────────────────────
  addRating: (id, rating, notes = '') =>
    put(`/api/data/applications/${id}`, { rating, rating_notes: notes }),

  // ── Tags — stored as text array on the row ──────────────────────────────
  addTag: async (id, tag) => {
    const cur = await get(`/api/data/applications/${id}`);
    const row = Array.isArray(cur) ? cur[0] : (cur?.data?.[0] || cur?.data || cur);
    const tags = Array.isArray(row?.tags) ? row.tags : [];
    if (!tags.includes(tag)) tags.push(tag);
    return put(`/api/data/applications/${id}`, { tags });
  },
  removeTag: async (id, tag) => {
    const cur = await get(`/api/data/applications/${id}`);
    const row = Array.isArray(cur) ? cur[0] : (cur?.data?.[0] || cur?.data || cur);
    const tags = (Array.isArray(row?.tags) ? row.tags : []).filter(t => t !== tag);
    return put(`/api/data/applications/${id}`, { tags });
  },

  // ── Timeline (stage changes + interviews + offers, sorted) ───────────────
  getTimeline: (id) => get(`/api/recruitment/applicant/${id}/timeline`),

  // ── Interviews ──────────────────────────────────────────────────────────
  getInterviews: (applicationId) =>
    get(`/api/data/application_interviews?application_id=${applicationId}&sort=scheduled_date&order=asc`),

  // Schedule an interview. Uses /api/data POST since the row goes into the
  // shared application_interviews table the candidate's VideoInterviewScreen
  // reads from.
  scheduleInterview: (applicationId, data) =>
    post(`/api/data/application_interviews`, {
      application_id: applicationId,
      interview_type:  data.type || data.interview_type || 'video',
      scheduled_date:  data.scheduled_date || data.scheduledDate || data.date,
      duration_minutes: data.duration_minutes || data.duration || 45,
      meeting_link:    data.meeting_link || data.meetingLink || data.meeting_url || null,
      location:        data.location || null,
      interviewer_name: data.interviewer_name || data.interviewerName || null,
      interviewer_id:  data.interviewer_id || null,
      interviewers:    data.interviewers || null,   // jsonb array
      instructions:    data.instructions || data.notes || null,
      round_number:    data.round_number || 1,
      status:          data.status || 'scheduled',
    }),

  updateInterview: (interviewId, data) =>
    put(`/api/data/application_interviews/${interviewId}`, data),

  cancelInterview: (interviewId, reason = '') =>
    put(`/api/data/application_interviews/${interviewId}`, { status: 'cancelled', cancellation_reason: reason }),

  // ── Offers ──────────────────────────────────────────────────────────────
  getOffers: (companyId) =>
    get(`/api/data/job_offers?company_id=${companyId}&sort=created_at&order=desc&limit=200`),

  getOffersByApplicant: (applicationId) =>
    get(`/api/data/job_offers?application_id=${applicationId}`),

  makeOffer: (applicantId, offerData) =>
    post(`/api/data/job_offers`, {
      application_id: applicantId,
      candidate_email: offerData.candidate_email || offerData.email,
      candidate_name:  offerData.candidate_name || offerData.name,
      job_id:          offerData.job_id,
      company_id:      offerData.company_id,
      position:        offerData.position || offerData.job_title,
      salary:          offerData.salary,
      currency:        offerData.currency || 'GBP',
      start_date:      offerData.start_date || offerData.startDate,
      benefits:        offerData.benefits || '',
      terms:           offerData.terms || '',
      expiry_date:     offerData.expiry_date || offerData.expiryDate,
      status:          offerData.status || 'sent',
      offer_letter:    offerData.offer_letter || null,
      ...offerData,
    }),

  updateOffer: (offerId, data) =>
    put(`/api/data/job_offers/${offerId}`, data),

  // ── Communication — best-effort; falls back to no-op when route missing
  sendEmail: (id, subject, body) =>
    post(`/api/recruitment/applicant/${id}/email`, { subject, body }).catch(() => ({ error: true, fallback: true })),
  sendSMS: (id, message) =>
    post(`/api/recruitment/applicant/${id}/sms`, { message }).catch(() => ({ error: true, fallback: true })),

  // ── Technical test ──────────────────────────────────────────────────────
  sendTechnicalTest: (id, data = {}) =>
    post(`/api/data/applicant_tests`, { application_id: id, ...data })
      .catch(() => put(`/api/data/applications/${id}`, { stage: 'technical', status: 'technical' })),

  // ── Search ──────────────────────────────────────────────────────────────
  searchApplicants: (q, companyId, params = {}) =>
    get(`/api/recruitment/pipeline?${qs({ company_id: companyId, q, ...params })}`),

  compareApplicants: (ids) =>
    Promise.all(ids.map(id => RecruitmentAPI.getApplicantById(id))),

  // ── Analytics ───────────────────────────────────────────────────────────
  getDashboardStats:   (cid) => get(`/api/recruitment/pipeline/stats?company_id=${cid}`),
  getConversionRates:  (cid) => get(`/api/recruitment/pipeline/stats?company_id=${cid}`),
  getSkillAnalytics:   (cid) =>
    get(`/api/data/applications?company_id=${cid}&limit=500`).then(rows => {
      const arr = Array.isArray(rows) ? rows : (rows?.data || []);
      const counts = {};
      arr.forEach(a => (Array.isArray(a.skills) ? a.skills : []).forEach(s => {
        const k = String(s).toLowerCase();
        counts[k] = (counts[k] || 0) + 1;
      }));
      return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 30).map(([skill, count]) => ({ skill, count }));
    }),

  // ── Company / jobs (used by the recruiter screens for header / filters) ─
  getCompanyProfile: (cid) =>
    get(`/api/data/company_profiles?id=${cid}&limit=1`).catch(() => get(`/api/data/company_profiles?company_id=${cid}&limit=1`)),

  getJobs: (cid, params = {}) =>
    get(`/api/data/jobs?${qs({ company_id: cid, limit: 200, ...params })}`),
};

// ── Stage / Status config ──────────────────────────────────────────────────
export const STAGES = [
  { key: 'new',         label: 'New',         icon: 'account-plus',          color: '#6366F1' },
  { key: 'reviewed',    label: 'Reviewed',    icon: 'eye-check',             color: '#3B82F6' },
  { key: 'shortlisted', label: 'Shortlisted', icon: 'account-star',          color: '#F59E0B' },
  { key: 'interviewed', label: 'Interviewed', icon: 'account-voice',         color: '#8B5CF6' },
  { key: 'technical',   label: 'Technical',   icon: 'code-braces',           color: '#EC4899' },
  { key: 'offered',     label: 'Offered',     icon: 'file-document-check',   color: '#1e7be2' },
  { key: 'hired',       label: 'Hired',       icon: 'check-decagram',        color: '#10B981' },
  { key: 'rejected',    label: 'Rejected',    icon: 'close-circle',          color: '#EF4444' },
];

export const STATUS_CFG = {
  New:         { color: '#3B82F6', bg: '#EFF6FF', icon: 'account-plus'          },
  Reviewed:    { color: '#8B5CF6', bg: '#F5F3FF', icon: 'account-check'         },
  Shortlisted: { color: '#10B981', bg: '#ECFDF5', icon: 'account-star'          },
  Interviewed: { color: '#F59E0B', bg: '#FFFBEB', icon: 'account-voice'         },
  Technical:   { color: '#EC4899', bg: '#FDF2F8', icon: 'code-braces'           },
  Offered:     { color: '#059669', bg: '#ECFDF5', icon: 'account-heart'         },
  Hired:       { color: '#0891B2', bg: '#F0FDFA', icon: 'account-check-outline' },
  Rejected:    { color: '#EF4444', bg: '#FEF2F2', icon: 'account-remove'        },
};

// ── Helpers (unchanged from before; kept stable so screens don't break) ────
export const fmtDate = d => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';
export const fmtDT   = d => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
export const timeAgo = ts => {
  if (!ts) return '—';
  const s = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (s < 60)    return 'Just now';
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};
export const initials = n => (n || '').split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('') || '?';
export const AVATAR_COLORS = ['#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444', '#0EA5E9', '#0e4e96'];
export const avatarColor = n => AVATAR_COLORS[(n || '').charCodeAt(0) % AVATAR_COLORS.length];

export const getStatusColor = s => {
  const st = (s || '').toLowerCase();
  if (['hired', 'accepted'].includes(st))             return '#10B981';
  if (['offered'].includes(st))                       return '#1e7be2';
  if (['shortlisted', 'reviewed', 'new', 'pending', 'applied', 'submitted'].includes(st)) return '#F59E0B';
  if (['rejected', 'withdrawn', 'declined'].includes(st)) return '#EF4444';
  if (['interviewed', 'technical', 'in_progress'].includes(st)) return '#8B5CF6';
  return '#6B7280';
};

// ── Applicant normaliser ──────────────────────────────────────────────────
// Handles all the field-name variants that come back from the different
// endpoints (the pipeline JOIN returns enriched fields; older code reads bare
// applications rows). One shape for the UI to consume.
export const normaliseApplicant = (raw) => {
  if (!raw) return null;
  const name = raw.candidate_name || raw.candidateName || raw.name
    || [raw.first_name || raw.firstName, raw.last_name || raw.lastName].filter(Boolean).join(' ')
    || 'Applicant';

  // skills can come as text[], JSON string, or comma-list
  const skills = (() => {
    const s = raw.skills || raw.required_skills;
    if (Array.isArray(s)) return s;
    if (typeof s === 'string') {
      try { const p = JSON.parse(s); if (Array.isArray(p)) return p; } catch {}
      return s.split(',').map(x => x.trim()).filter(Boolean);
    }
    return [];
  })();

  return {
    ...raw,
    id:            String(raw.id || raw._id || ''),
    name,
    email:         raw.candidate_email || raw.email || '',
    phone:         raw.candidate_phone || raw.phone || '',
    avatar:        raw.candidate_avatar || raw.avatar || raw.profile_picture || raw.photo
                   || `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random&size=128`,
    stage:         raw.stage || raw.status || 'new',
    status:        raw.status || raw.stage || 'submitted',
    jobId:         String(raw.job_id || raw.jobId || ''),
    jobTitle:      raw.job_title || raw.jobTitle || raw.position || '',     // NEW: prefers JOINed job_title over candidate's current_title
    employerName:  raw.employer_name || raw.company_name || raw.company || '',
    employerLogo:  raw.employer_logo || raw.company_logo || null,
    currentRole:   raw.current_title || raw.current_role || raw.currentRole || '',
    currentCompany:raw.current_company || raw.currentCompany || '',
    location:      raw.candidate_location || raw.location || raw.city || '',
    jobLocation:   raw.job_location || '',
    experience:    raw.experience_years || raw.years_experience || raw.experience || 0,
    skills,
    aiScore:       raw.match_score || raw.ai_score || raw.aiScore || raw.matchScore || raw.overall_score || null,
    rating:        raw.rating || raw.stars || 0,
    isBookmarked:  !!(raw.is_starred || raw.is_bookmarked || raw.bookmarked),
    isFlagged:     !!(raw.is_flagged || raw.flagged),
    tags:          Array.isArray(raw.tags) ? raw.tags : [],
    notes:         raw.notes || raw.internal_notes || '',
    coverLetter:   raw.cover_letter || raw.coverLetter || '',
    resumeUrl:     raw.resume_url || raw.cv_url || raw.resumeUrl || '',
    appliedAt:     raw.applied_date || raw.applied_at || raw.created_at || raw.appliedDate || '',
    updatedAt:     raw.updated_at || raw.updatedDate || '',
    salary:        raw.salary_expectations || raw.salary_expectation || raw.salaryExpectation || raw.expected_salary || '',
    noticePeriod:  raw.notice_period || raw.noticePeriod || '',
    education:     raw.education || raw.education_level || '',
    linkedin:      raw.candidate_linkedin || raw.linkedin_url || raw.linkedIn || raw.linkedin || '',
    portfolio:     raw.portfolio_url || raw.portfolio || '',
    // Interview status from the pipeline JOIN aggregate
    interviewsScheduled: Number(raw.interviews_scheduled || 0),
    interviewsCompleted: Number(raw.interviews_completed || 0),
    nextInterviewAt:     raw.next_interview_at || null,
  };
};

// ── Company data loader (used by every recruiter screen header) ───────────
export const loadCompanyData = async (setLogo, setName, setId) => {
  try {
    // Fast path: cached profile
    const cached = await AsyncStorage.getItem('company_profile');
    if (cached) {
      const p = JSON.parse(cached);
      setLogo?.(p.logo || null);
      if (p.name || p.company_name) setName?.(p.name || p.company_name || '');
    }
    const cid = parseInt(await AsyncStorage.getItem('company_id') || '0', 10);
    if (cid) {
      setId?.(cid);
      const res = await RecruitmentAPI.getCompanyProfile(cid);
      const rows = Array.isArray(res) ? res : (res?.data || []);
      const p = Array.isArray(rows) ? rows[0] : rows;
      if (p) {
        setLogo?.(p.logo || null);
        if (p.name || p.company_name) setName?.(p.name || p.company_name || '');
        await AsyncStorage.setItem('company_profile', JSON.stringify(p));
      }
    }
  } catch (e) {
    console.warn('loadCompanyData:', e?.message);
  }
};

// ── Backwards-compat exports ──────────────────────────────────────────────
// Older screens use these names. Re-exported as aliases so nothing breaks
// during the migration period.
export const API_BASE = 'https://api.jobeng.ai';
export const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
export const rApi = async (path, method = 'GET', body = null) => {
  // Thin wrapper that delegates to the canonical apiFetch. Preserves the
  // old (path, method, body) signature some screens already use.
  return apiFetch(path, { method, body: body ? JSON.stringify(body) : undefined });
};

export default RecruitmentAPI;
